import cx from 'classnames';
import { FormV2 } from 'components/ui';
import { ChevronDown } from 'ethena-icons';
import { EthenaDate } from 'lib/helpers/dateHelper';
import _ from 'lodash';
import {
  createElement,
  forwardRef,
  InputHTMLAttributes,
  Ref,
  SyntheticEvent,
  useState,
} from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import css from './DatePicker.module.scss';

interface IDatePickerInputProps extends InputHTMLAttributes<HTMLInputElement> {
  errorMessage?: string;
  isInvalid?: boolean;
  isMenuOpen?: boolean;
}

const DatePickerInput = (
  {
    className,
    disabled,
    errorMessage,
    isInvalid,
    isMenuOpen,
    onChange,
    onClick,
    onKeyDown,
    placeholder,
    value,
  }: IDatePickerInputProps,
  ref: Ref<HTMLInputElement>
) => {
  return (
    <>
      <div
        className={cx(css.datePickerInput, className, {
          [css.disabled]: disabled,
          [css.focus]: isMenuOpen,
          [css.invalid]: isInvalid,
          [css.open]: isMenuOpen,
        })}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        <input
          className="w-100"
          disabled={disabled}
          onChange={onChange}
          placeholder={placeholder}
          ref={ref}
          value={value}
        />
        <ChevronDown />
      </div>
      <FormV2.Control.Feedback type="invalid">
        {errorMessage}
      </FormV2.Control.Feedback>
    </>
  );
};

interface IUtcDatePickerProps extends Omit<ReactDatePickerProps, 'onChange'> {
  errorMessage?: string;
  inputClassName?: string;
  isInvalid?: boolean;
  onChange: (
    date: string | string[],
    event: SyntheticEvent<any, Event>
  ) => void;
}

/**
 * DatePicker that takes in UTC dates and returns out UTC dates. Context for
 * why we need this: https://github.com/Hacker0x01/react-datepicker/issues/1787
 */
export const UtcDatePicker = ({
  calendarClassName,
  dateFormat = 'MMMM d, yyyy',
  endDate,
  errorMessage,
  inputClassName,
  isInvalid,
  maxDate,
  minDate,
  onChange,
  popperPlacement = 'bottom-end',
  selected,
  startDate,
  wrapperClassName,
  ...rest
}: IUtcDatePickerProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const localDateToUtcDate = (date: Date): string =>
    date ? new EthenaDate(date).toPureDateFormat() : undefined;

  const utcDateToLocalDate = (date: Date): Date =>
    date ? new EthenaDate(date).toLocalStartOfDate() : undefined;

  return (
    <DatePicker
      calendarClassName={cx(css.datePickerCalendar, calendarClassName)}
      clearButtonClassName={css.clearButton}
      customInput={createElement(forwardRef(DatePickerInput), {
        className: inputClassName,
        isInvalid,
        isMenuOpen,
        errorMessage,
      })}
      dateFormat={dateFormat}
      enableTabLoop={false}
      endDate={utcDateToLocalDate(endDate)}
      maxDate={utcDateToLocalDate(maxDate)}
      minDate={utcDateToLocalDate(minDate)}
      onCalendarClose={() => setIsMenuOpen(false)}
      onCalendarOpen={() => setIsMenuOpen(true)}
      onChange={(dates, event) => {
        _.isArray(dates)
          ? onChange(
              _.map(dates, (x) => localDateToUtcDate(x)),
              event
            )
          : onChange(localDateToUtcDate(dates), event);
      }}
      popperPlacement={popperPlacement}
      selected={utcDateToLocalDate(selected)}
      showPopperArrow={false}
      startDate={utcDateToLocalDate(startDate)}
      wrapperClassName={cx(css.datePickerWrapper, wrapperClassName)}
      {...rest}
    />
  );
};
